import type {
  ClassifiedNode,
  InsightReport,
  KnowledgeCatalog,
  KnowledgeNode,
  LocalizedText,
  RelationExplanation,
  RelationRule,
  SelectionAttempt,
} from "./types";

const pairKey = (left: string, right: string): string =>
  [left, right].sort().join("::");

const compareIds = (left: string, right: string): number =>
  left.localeCompare(right, "en");

export function buildRelationIndex(
  catalog: KnowledgeCatalog,
): Map<string, RelationRule> {
  const index = new Map<string, RelationRule>();
  for (const relation of catalog.relations) {
    index.set(pairKey(relation.left, relation.right), relation);
  }
  return index;
}

export function getRelation(
  index: ReadonlyMap<string, RelationRule>,
  left: string,
  right: string,
): RelationRule | undefined {
  return index.get(pairKey(left, right));
}

function explain(
  relation: RelationRule,
  selectedNodeId: string,
  candidateNodeId: string,
): RelationExplanation {
  return {
    relationId: relation.id,
    selectedNodeId,
    candidateNodeId,
    kind: relation.kind,
    reason: relation.reason,
    action: relation.action,
    conditions: [...relation.conditions],
    evidenceIds: [...relation.evidenceIds],
    hardBlock: relation.hardBlock,
    strength: relation.strength,
    ruleVersion: relation.ruleVersion,
    lastReviewed: relation.lastReviewed,
  };
}

function explainUnknown(
  selectedNodeId: string,
  candidateNodeId: string,
): RelationExplanation {
  return {
    relationId: `UNASSERTED-${selectedNodeId}-${candidateNodeId}`,
    selectedNodeId,
    candidateNodeId,
    kind: "unknown",
    reason: {
      en: "The current catalog contains no relationship assertion for this pair.",
      zh: "当前知识库没有声明这组节点之间的关系。",
    },
    action: {
      en: "Treat the relationship as unknown and define evidence plus verification conditions.",
      zh: "将该关系视为未知，并补充证据与验证条件。",
    },
    conditions: [],
    evidenceIds: [],
    hardBlock: false,
    strength: "synthetic",
    ruleVersion: "unasserted",
    lastReviewed: "not-applicable",
  };
}

export function classifyNodes(
  catalog: KnowledgeCatalog,
  selectedInput: Iterable<string>,
): ClassifiedNode[] {
  const selectedIds = new Set(selectedInput);
  const relationIndex = buildRelationIndex(catalog);

  return [...catalog.nodes]
    .sort((left, right) => compareIds(left.id, right.id))
    .map((node): ClassifiedNode => {
      if (selectedIds.has(node.id)) {
        return { node, state: "selected", explanations: [] };
      }
      if (selectedIds.size === 0) {
        return { node, state: "available", explanations: [] };
      }

      const explanations = [...selectedIds]
        .sort(compareIds)
        .map((selectedId) => {
          const relation = getRelation(relationIndex, selectedId, node.id);
          return relation
            ? explain(relation, selectedId, node.id)
            : explainUnknown(selectedId, node.id);
        });

      const unresolved = explanations.filter((item) => item.kind !== "explore");

      const hardConflicts = explanations.filter(
        (item) => item.kind === "conflict" && item.hardBlock,
      );
      if (hardConflicts.length > 0) {
        return { node, state: "blocked", explanations: unresolved };
      }

      const reviewConflicts = explanations.filter(
        (item) => item.kind === "conflict" && !item.hardBlock,
      );
      if (reviewConflicts.length > 0) {
        return { node, state: "review", explanations: unresolved };
      }

      const conditions = explanations.filter(
        (item) => item.kind === "conditional",
      );
      if (conditions.length > 0) {
        return { node, state: "conditional", explanations: unresolved };
      }

      const unknowns = explanations.filter((item) => item.kind === "unknown");
      if (unknowns.length > 0) {
        return { node, state: "unknown", explanations: unknowns };
      }

      const explorations = explanations.filter((item) => item.kind === "explore");
      if (explorations.length > 0) {
        return { node, state: "explore", explanations: explorations };
      }

      return { node, state: "unknown", explanations: [] };
    });
}

export function attemptNodeSelection(
  catalog: KnowledgeCatalog,
  selectedInput: Iterable<string>,
  nodeId: string,
  options: { allowReview?: boolean } = {},
): SelectionAttempt {
  const selectedIds = new Set(selectedInput);
  if (selectedIds.has(nodeId)) {
    selectedIds.delete(nodeId);
    return {
      accepted: true,
      selectedIds: [...selectedIds].sort(compareIds),
      state: "selected",
      explanations: [],
      requiresReview: false,
    };
  }

  const classified = classifyNodes(catalog, selectedIds).find(
    (item) => item.node.id === nodeId,
  );
  if (!classified) {
    throw new Error(`Unknown node: ${nodeId}`);
  }
  if (classified.state === "blocked") {
    return {
      accepted: false,
      selectedIds: [...selectedIds].sort(compareIds),
      state: classified.state,
      explanations: classified.explanations,
      requiresReview: false,
    };
  }
  if (
    ["review", "conditional", "unknown"].includes(classified.state) &&
    !options.allowReview
  ) {
    return {
      accepted: false,
      selectedIds: [...selectedIds].sort(compareIds),
      state: classified.state,
      explanations: classified.explanations,
      requiresReview: true,
    };
  }

  selectedIds.add(nodeId);
  return {
    accepted: true,
    selectedIds: [...selectedIds].sort(compareIds),
    state: classified.state,
    explanations: classified.explanations,
    requiresReview: false,
  };
}

function selectedNodes(
  catalog: KnowledgeCatalog,
  selectedIds: Set<string>,
): KnowledgeNode[] {
  return catalog.nodes
    .filter((node) => selectedIds.has(node.id))
    .sort((left, right) => compareIds(left.id, right.id));
}

const localized = (en: string, zh: string): LocalizedText => ({ en, zh });

export function buildInsightReport(
  catalog: KnowledgeCatalog,
  selectedInput: Iterable<string>,
): InsightReport {
  const selectedIds = new Set(selectedInput);
  const nodes = selectedNodes(catalog, selectedIds);
  const relationIndex = buildRelationIndex(catalog);
  const conflicts: RelationRule[] = [];
  const conditions: RelationRule[] = [];
  const unknownPairs: { left: string; right: string }[] = [];
  const evidenceIds = new Set<string>();

  for (const node of nodes) {
    for (const evidenceId of node.evidenceIds) evidenceIds.add(evidenceId);
  }

  for (let leftIndex = 0; leftIndex < nodes.length; leftIndex += 1) {
    const left = nodes[leftIndex];
    if (!left) continue;
    for (let rightIndex = leftIndex + 1; rightIndex < nodes.length; rightIndex += 1) {
      const right = nodes[rightIndex];
      if (!right) continue;
      const relation = getRelation(relationIndex, left.id, right.id);
      if (!relation) {
        unknownPairs.push({ left: left.id, right: right.id });
        continue;
      }
      for (const evidenceId of relation.evidenceIds) evidenceIds.add(evidenceId);
      if (relation.kind === "conflict") conflicts.push(relation);
      if (relation.kind === "conditional") conditions.push(relation);
    }
  }

  const matchedEffects = catalog.effects
    .filter((effect) => {
      if (
        conflicts.length > 0 ||
        conditions.length > 0 ||
        unknownPairs.length > 0
      ) {
        return false;
      }
      if (!effect.requires.every((id) => selectedIds.has(id))) return false;
      for (let leftIndex = 0; leftIndex < effect.requires.length; leftIndex += 1) {
        const left = effect.requires[leftIndex];
        if (!left) return false;
        for (
          let rightIndex = leftIndex + 1;
          rightIndex < effect.requires.length;
          rightIndex += 1
        ) {
          const right = effect.requires[rightIndex];
          if (!right) return false;
          if (getRelation(relationIndex, left, right)?.kind !== "explore") {
            return false;
          }
        }
      }
      return true;
    })
    .sort((left, right) => compareIds(left.id, right.id));
  for (const effect of matchedEffects) {
    for (const evidenceId of effect.evidenceIds) evidenceIds.add(evidenceId);
  }

  const nextChecks: LocalizedText[] = [];
  if (nodes.length < 2) {
    nextChecks.push(
      localized(
        "Select at least two roles to inspect a relationship.",
        "至少选择两个角色，才能检查它们之间的关系。",
      ),
    );
  } else {
    nextChecks.push(
      localized(
        "Define real material identity, grade, intended use, phase, pH, temperature, and order context.",
        "明确真实材料身份、等级、用途、相态、pH、温度和加入顺序背景。",
      ),
    );
    nextChecks.push(
      localized(
        "Create bench acceptance criteria for physical, chemical, and biological observations.",
        "为物理、化学与生物观察建立台架实验验收标准。",
      ),
    );
  }
  if (selectedIds.has("PRES-H")) {
    nextChecks.push(
      localized(
        "Plan an appropriate preservation verification protocol; this node never establishes efficacy.",
        "规划合适的防腐验证方案；此节点绝不证明有效性。",
      ),
    );
  }
  if (selectedIds.has("BIO-L")) {
    nextChecks.push(
      localized(
        "Define identity-specific biological activity and stability endpoints.",
        "定义针对具体身份的生物活性与稳定性终点。",
      ),
    );
  }

  return {
    selectedNodes: nodes,
    functionalRoles: [...new Set(nodes.flatMap((node) => node.roles))].sort(compareIds),
    matchedEffects,
    conflicts: conflicts.sort((left, right) => compareIds(left.id, right.id)),
    conditions: conditions.sort((left, right) => compareIds(left.id, right.id)),
    unknownPairs: unknownPairs.sort((left, right) =>
      pairKey(left.left, left.right).localeCompare(pairKey(right.left, right.right)),
    ),
    nextChecks,
    evidenceIds: [...evidenceIds].sort(compareIds),
    conclusionAllowed: false,
  };
}
